import type { ChatCompletionRequestMessage } from "@inference-net/cf-compatible-models/openai";
import {
  ChatCompletionMessageToolCallsSchema,
  ChatCompletionRequestMessageSchema,
} from "@inference-net/cf-compatible-models/openai";
import { z } from "zod";

import type { ResponseStreamChunk } from "../responses/responses.models";
import { ChatTemplateKwargsSchema } from "./reasoning.models";

export {
  CHAT_COMPLETION_REQUEST_MESSAGE_ROLES,
  ChatCompletionMessageToolCallSchema,
  ChatCompletionMessageCustomToolCallSchema,
  ChatCompletionMessageFunctionToolCallSchema,
  ChatCompletionMessageToolCallsSchema,
  ChatCompletionRequestAssistantMessageSchema,
  ChatCompletionRequestDeveloperMessageSchema,
  ChatCompletionRequestFunctionMessageSchema,
  ChatCompletionRequestAssistantContentPartSchema,
  ChatCompletionRequestMessageContentPartFileSchema,
  ChatCompletionRequestMessageContentPartImageSchema,
  ChatCompletionRequestMessageContentPartInputAudioSchema,
  ChatCompletionRequestMessageContentPartRefusalSchema,
  ChatCompletionRequestMessageContentPartSchema,
  ChatCompletionRequestMessageContentPartTextSchema,
  ChatCompletionRequestMessageSchema,
  ChatCompletionRequestSystemMessageSchema,
  ChatCompletionRequestTextContentPartSchema,
  ChatCompletionRequestToolContentPartSchema,
  ChatCompletionRequestToolMessageSchema,
  ChatCompletionRequestUnknownContentPartSchema,
  ChatCompletionRequestUserContentPartSchema,
  ChatCompletionRequestUserMessageSchema,
} from "@inference-net/cf-compatible-models/openai";

export type {
  ChatCompletionMessageCustomToolCall,
  ChatCompletionMessageFunctionToolCall,
  ChatCompletionMessageToolCall,
  ChatCompletionMessageToolCalls,
  ChatCompletionRequestAssistantContentPart,
  ChatCompletionRequestAssistantMessage,
  ChatCompletionRequestDeveloperMessage,
  ChatCompletionRequestFunctionMessage,
  ChatCompletionRequestMessage,
  ChatCompletionRequestMessageContentPart,
  ChatCompletionRequestMessageContentPartFile,
  ChatCompletionRequestMessageContentPartImage,
  ChatCompletionRequestMessageContentPartInputAudio,
  ChatCompletionRequestMessageContentPartRefusal,
  ChatCompletionRequestMessageContentPartText,
  ChatCompletionRequestSystemMessage,
  ChatCompletionRequestTextContentPart,
  ChatCompletionRequestToolContentPart,
  ChatCompletionRequestToolMessage,
  ChatCompletionRequestUnknownContentPart,
  ChatCompletionRequestUnknownMessage,
  ChatCompletionRequestUserContentPart,
  ChatCompletionRequestUserMessage,
} from "@inference-net/cf-compatible-models/openai";

/** ******************************************************************************
 *  JSON Schema Object
 ******************************************************************************* */

// Function parameters and json_schema response formats carry almost any JSON
// schema (https://platform.openai.com/docs/guides/function-calling#defining-functions).
// Writing a zod schema for that is impractical, so we accept any object and
// leave compilability to the grammar backend.
export const JsonSchemaObjectSchema = z.record(z.any());

export type JsonSchemaObject = z.infer<typeof JsonSchemaObjectSchema>;

/** ******************************************************************************
 *  Function Object
 ******************************************************************************* */

export const FunctionObjectSchema = z
  .object({
    description: z.string().optional(),
    name: z.string(),
    parameters: JsonSchemaObjectSchema.optional(),
    strict: z.boolean().optional(),
  })
  .passthrough();

export type FunctionObject = z.infer<typeof FunctionObjectSchema>;

/** ******************************************************************************
 *  Chat Completion Tool
 ******************************************************************************* */

export const ChatCompletionToolSchema = z
  .object({
    type: z.literal("function"),
    function: FunctionObjectSchema,
  })
  .passthrough();

export type ChatCompletionTool = z.infer<typeof ChatCompletionToolSchema>;

/** ******************************************************************************
 *  Chat Completion Named Tool Choice
 ******************************************************************************* */

export const ChatCompletionNamedToolChoiceSchema = z
  .object({
    type: z.literal("function"),
    function: z.object({ name: z.string() }).passthrough(),
  })
  .passthrough();

export type ChatCompletionNamedToolChoice = z.infer<
  typeof ChatCompletionNamedToolChoiceSchema
>;

/** ******************************************************************************
 *  Chat Completion Tool Choice Option
 ******************************************************************************* */

export const ChatCompletionToolChoiceOptionSchema = z.union([
  z.enum(["none", "auto", "required"]),
  ChatCompletionNamedToolChoiceSchema,
]);

export type ChatCompletionToolChoiceOption = z.infer<
  typeof ChatCompletionToolChoiceOptionSchema
>;

/** ******************************************************************************
 *  Chat Completion Function Call Option
 ******************************************************************************* */

export const ChatCompletionFunctionCallOption = z
  .object({ name: z.string() })
  .passthrough();

export type ChatCompletionFunctionCall = z.infer<
  typeof ChatCompletionFunctionCallOption
>;

/** ******************************************************************************
 *  Chat Completion Functions
 ******************************************************************************* */

export const ChatCompletionFunctionsSchema = z
  .object({
    description: z.string().optional(),
    name: z.string(),
    parameters: JsonSchemaObjectSchema.optional(),
    strict: z.boolean().optional(),
  })
  .passthrough();

export type ChatCompletionFunctions = z.infer<
  typeof ChatCompletionFunctionsSchema
>;

/** ******************************************************************************
 *  Chat Completion Request
 ******************************************************************************* */

/**
 * Mirrors OpenAI's `response_format`. The `json_schema` variant validates only
 * the envelope: `strict` and `description` are optional/nullable exactly as in
 * OpenAI's SDK types, and `schema` is any JSON-schema object. Whether the
 * schema itself is compilable is the grammar backend's call (xgrammar accepts
 * optional properties, non-object roots, `$defs`, ...); an uncompilable schema
 * comes back through the worker error path as a 400.
 */
const ResponseFormatSchema = z.union([
  z
    .object({
      type: z.enum(["text", "json_object"]),
    })
    .passthrough(),
  z
    .object({
      type: z.literal("json_schema"),
      json_schema: z
        .object({
          name: z.string(),
          description: z.string().nullish(),
          strict: z.boolean().nullish(),
          schema: JsonSchemaObjectSchema,
        })
        .passthrough(),
    })
    .passthrough(),
]);

/** ******************************************************************************
 *  Completion Request Metadata
 ******************************************************************************* */

// TODO: Split this out somewhere else since its used by embeddings as well
export const MetadataSchema = z
  .object({
    webhook_id: z.string().optional(),
  })
  .catchall(z.any());

export type Metadata = z.infer<typeof MetadataSchema>;

/**
 * Effort levels accepted by the OpenAI-style `reasoning_effort` param.
 * "none" disables reasoning (and on some models re-enables sampling params);
 * "max" is Anthropic-only; availability of individual levels varies per model.
 */
export const REASONING_EFFORT_LEVELS = [
  "none",
  "minimal",
  "low",
  "medium",
  "high",
  "xhigh",
  "max",
] as const;

export const ReasoningEffortLevelSchema = z.enum(REASONING_EFFORT_LEVELS);

export type ReasoningEffortLevel = z.infer<typeof ReasoningEffortLevelSchema>;

/** Verbosity levels accepted by the gpt-5 family's `verbosity` param. */
export const VERBOSITY_LEVELS = ["low", "medium", "high"] as const;

export const VerbosityLevelSchema = z.enum(VERBOSITY_LEVELS);

export type VerbosityLevel = z.infer<typeof VerbosityLevelSchema>;

const baseCompletionSchema = z.object({
  model: z.string(),
  frequency_penalty: z.number().gte(-2).lte(2).nullish(),
  instance_id: z.string().nullish(),
  logit_bias: z.record(z.number().int()).nullish(),
  logprobs: z.boolean().nullish(),
  top_logprobs: z.number().min(0).max(5).nullish(),
  max_tokens: z.number().int().positive().nullish(),
  max_completion_tokens: z.number().int().positive().nullish(),
  n: z.number().int().gte(1).lte(128).nullish().default(1),
  presence_penalty: z.number().gte(-2).lte(2).nullish(),
  response_format: ResponseFormatSchema.optional(),
  seed: z
    .number()
    .int()
    .gte(-9223372036854776000)
    .lte(9223372036854776000)
    .nullish(),
  stop: z.union([z.string(), z.array(z.string())]).optional(),
  stream: z.boolean().nullish(),
  temperature: z.number().gte(0).lte(2).nullish().default(1),
  top_p: z.number().gte(0).lte(1).nullish().default(1),
  user: z.string().optional(),
  function_call: z
    .union([z.enum(["none", "auto"]), ChatCompletionFunctionCallOption])
    .optional(),
  functions: z.array(ChatCompletionFunctionsSchema).min(1).max(128).optional(),

  // Additional parameters which are supported by OpenAI but not VLLM.
  top_k: z.number().int().gte(1).or(z.literal(-1)).optional(),
  min_p: z.number().gte(0).lte(1).optional(),

  // Reasoning-model effort control. OpenAI reasoning models take it natively;
  // for Anthropic models the LiteLLM gateway maps it to extended-thinking
  // budgets. Not supported by cluster-served open models (use
  // chat_template_kwargs.enable_thinking there).
  reasoning_effort: ReasoningEffortLevelSchema.nullish(),

  // Output length/detail control for the gpt-5 model family.
  verbosity: VerbosityLevelSchema.nullish(),

  // Metadata field for webhook_id and other custom metadata
  metadata: MetadataSchema.optional(),

  extra_body: z.record(z.any()).optional(),

  // This is the SUPPORTED, end-to-end mechanism for toggling model "thinking"
  // per request, e.g. { "chat_template_kwargs": { "enable_thinking": false } }
  // (INF-3673). `reasoning: { enabled: false }` is a no-op on vLLM/SGLang and
  // must not be relied upon. Modeling the field explicitly guards against a
  // future allowlist refactor silently dropping it (which would re-enable
  // thinking and shift tool-call behavior).
  chat_template_kwargs: ChatTemplateKwargsSchema.nullish(),
});

type BaseCompletionRequest = z.infer<typeof baseCompletionSchema>;

export type ChatCompletionStreamOptions = {
  include_usage?: boolean;
  /**
   * vLLM/sglang extension: cumulative usage on every chunk instead of only
   * the final one. Engine-bound only — generation-service sets it for
   * deployments billing cancelled generations and the router strips per-chunk
   * usage before chunks reach the client.
   */
  continuous_usage_stats?: boolean;
};

const baseCompletionPassthroughSchema = baseCompletionSchema.passthrough();

export type V1ChatCompletionRequest = BaseCompletionRequest & {
  messages: ChatCompletionRequestMessage[];
  tools?: ChatCompletionTool[] | null;
  tool_choice?: ChatCompletionToolChoiceOption | null;
  stream_options?: ChatCompletionStreamOptions;
};

/**
 * Wire-input companion to `V1ChatCompletionRequest`: the schema fills `n`,
 * `temperature`, and `top_p` via defaults, so a client building a request may
 * omit them — and sometimes must, when a provider pins its own default (e.g.
 * Kimi K3 rejects an explicit `n`).
 */
export type V1ChatCompletionRequestInput = Omit<
  V1ChatCompletionRequest,
  "n" | "temperature" | "top_p"
> & {
  n?: number | null;
  temperature?: number | null;
  top_p?: number | null;
};

// per (roughly) this: https://platform.openai.com/docs/api-reference/chat/create
// but actually closer to this?: https://github.com/ollama/ollama/blob/581a4a55532e2a28177bcaffd722a2bf298f967d/openai/openai.go#L78
export const V1ChatCompletionRequestSchema =
  baseCompletionPassthroughSchema.extend({
    messages: z.array(ChatCompletionRequestMessageSchema).min(1),
    tools: z.array(ChatCompletionToolSchema).optional().nullable(),
    tool_choice: ChatCompletionToolChoiceOptionSchema.optional().nullable(),
    stream_options: z
      .object({ include_usage: z.boolean().optional() })
      .passthrough()
      .optional(),
  }) as z.ZodType<V1ChatCompletionRequest>;

// per (roughly) this: https://platform.openai.com/docs/api-reference/completions/create#completions/create
// but actually this: https://github.com/ollama/ollama/blob/581a4a55532e2a28177bcaffd722a2bf298f967d/openai/openai.go#L113
// (ollama doesn't support best_of and echo, for example)
export type V1TextCompletionRequest = BaseCompletionRequest & {
  prompt: string;
  stream_options?: ChatCompletionStreamOptions;
};

export const V1TextCompletionRequestSchema =
  baseCompletionPassthroughSchema.extend({
    prompt: z.string(),
    stream_options: z
      .object({ include_usage: z.boolean().optional() })
      .passthrough()
      .optional(),
    // Note: Although suffix is listed in the Ollana API docs *and* in the OpenAI API docs, it doesn't work in Ollama.
    //suffix: z.string().nullish(),
  }) as z.ZodType<V1TextCompletionRequest>;

/** ******************************************************************************
 *  Chat Completion Response Message
 ******************************************************************************* */

export const ChatCompletionResponseMessageSchema = z.object({
  content: z.string().nullable(),
  reasoning_content: z.string().nullish(),
  reasoning: z.string().nullish(),
  tool_calls: ChatCompletionMessageToolCallsSchema.optional().nullable(),
  role: z.literal("assistant"),
  function_call: z
    .object({ arguments: z.string(), name: z.string() })
    .optional(),
});

export type ChatCompletionResponseMessage = z.infer<
  typeof ChatCompletionResponseMessageSchema
>;

/** ******************************************************************************
 *  Token Logprob
 ******************************************************************************* */

export const LogprobsContentSchema = z.object({
  token: z.string(),
  logprob: z.number(),
  bytes: z.array(z.number()).nullable(),
  top_logprobs: z.array(
    z.object({
      token: z.string(),
      logprob: z.number(),
      bytes: z.array(z.number()).nullable(),
    }),
  ),
});

export type LogprobsContent = z.infer<typeof LogprobsContentSchema>;

export const LogprobsSchema = z.object({
  content: z.array(LogprobsContentSchema),
});

export type Logprobs = z.infer<typeof LogprobsSchema>;

/** ******************************************************************************
 *  Token Logprob (Token IDs)
 ******************************************************************************* */

export const LogprobsWithTokenIdsContentSchema = z.object({
  token_id: z.number(),
  logprob: z.number(),
  top_logprobs: z.array(
    z.object({
      token_id: z.number(),
      logprob: z.number(),
    }),
  ),
});

export type LogprobsWithTokenIdsContent = z.infer<
  typeof LogprobsWithTokenIdsContentSchema
>;

export const LogprobsWithTokenIdsSchema = z.object({
  content: z.array(LogprobsWithTokenIdsContentSchema),
});

export type LogprobsWithTokenIds = z.infer<typeof LogprobsWithTokenIdsSchema>;

/** ******************************************************************************
 *  Completion Usage
 ******************************************************************************* */

export const CompletionUsageSchema = z.object({
  completion_tokens: z.number().int(),
  prompt_tokens: z.number().int(),
  total_tokens: z.number().int(),
  // NOTE: In the future this wont be nullable, but for now not all inference engines return it
  // some just combine the reasoning tokens into the completion tokens
  completion_tokens_details: z
    .object({
      reasoning_tokens: z.number().int(),
      accepted_prediction_tokens: z.number().int(),
      rejected_prediction_tokens: z.number().int(),
    })
    .nullish(),
  // sglang (own fleet) reports reasoning at the top level instead of nesting
  // it under completion_tokens_details. It counts generated tokens through the
  // think-end marker and is included in completion_tokens; total_tokens stays
  // prompt + completion.
  reasoning_tokens: z.number().int().nullish(),
  // Additional field that may be present in responses but always null for now
  prompt_tokens_details: z.unknown().nullish(),
});

export type CompletionUsage = z.infer<typeof CompletionUsageSchema>;

/** ******************************************************************************
 *  Create Chat Completion Response
 ******************************************************************************* */

export const FinishReasonSchema = z.enum([
  "stop",
  "length",
  "tool_calls",
  "content_filter",
  "function_call",
  "error",
]);

export const TextCompletionResponseChoiceSchema = z.object({
  text: z.string(),
  index: z.number().int(),
  finish_reason: FinishReasonSchema,
  logprobs: LogprobsSchema.nullable(),
});

export type TextCompletionResponseChoice = z.infer<
  typeof TextCompletionResponseChoiceSchema
>;

export const ChatCompletionResponseChoiceSchema = z.object({
  finish_reason: FinishReasonSchema,
  index: z.number().int(),
  message: ChatCompletionResponseMessageSchema,
  logprobs: LogprobsSchema.nullable().optional(),

  // Additional field that may be present in responses
  matched_stop: z.string().nullish(),
});

export type ChatCompletionResponseChoice = z.infer<
  typeof ChatCompletionResponseChoiceSchema
>;

const BaseCompletionResponseSchema = z.object({
  id: z.string(),
  created: z.number().int(),
  model: z.string(),
  system_fingerprint: z.string().optional(),
  usage: CompletionUsageSchema.optional(),

  metadata: MetadataSchema.optional(),
});

export const V1ChatCompletionResponseSchema =
  BaseCompletionResponseSchema.extend({
    choices: z.array(ChatCompletionResponseChoiceSchema),
    object: z.literal("chat.completion"),
  });

export type V1ChatCompletionResponse = z.infer<
  typeof V1ChatCompletionResponseSchema
>;

export const V1CompletionResponseSchema = BaseCompletionResponseSchema.extend({
  choices: z.array(TextCompletionResponseChoiceSchema),
  object: z.literal("text_completion"),
});

export type V1CompletionResponse = z.infer<typeof V1CompletionResponseSchema>;

/** ******************************************************************************
 *  Create Chat Completion Chunk Response
 ******************************************************************************* */

export const ChatCompletionChunkFunctionCallSchema = z.object({
  arguments: z.string().optional(),
  name: z.string().optional(),
});

export type ChatCompletionChunkFunctionCall = z.infer<
  typeof ChatCompletionChunkFunctionCallSchema
>;

export const ChatCompletionChunkFunctionSchema = z.object({
  // Optional: vLLM's Hermes tool parser (kolibri-1) omits `arguments` on the
  // first tool-call delta, which carries only the name.
  arguments: z.string().optional(),
  // Nullable in addition to optional: providers send `"name": null` on
  // tool-call continuation deltas (e.g. glm-5.3-flash, kimi-k3).
  name: z.string().nullish(),
});

export type ChatCompletionChunkFunction = z.infer<
  typeof ChatCompletionChunkFunctionSchema
>;

export const ChatCompletionChunkToolCallSchema = z.object({
  index: z.number().int(),
  // Nullable: providers send `"id": null` on continuation deltas (the id is
  // only emitted on the first tool-call chunk of a stream).
  id: z.string().nullish(),
  function: ChatCompletionChunkFunctionSchema,
  // Streaming tool-call deltas are forwarded from upstream workers without
  // re-validation, so `type` reflects whatever the provider sent: it may be
  // absent (some fine-tuned providers omit it on continuation deltas, esp. the
  // 2nd+ call in a multi-tool stream) or, in principle, an unrecognized variant.
  // Model it as an optional string rather than a strict `z.literal("function")`
  // so the accumulator can distinguish "missing" (default to function) from
  // "present but unknown" (alert) without lying about the wire shape.
  type: z.string().optional(),
});

export type ChatCompletionChunkToolCall = z.infer<
  typeof ChatCompletionChunkToolCallSchema
>;

export const contentRolesSchema = z.union([
  z.literal("system"),
  z.literal("user"),
  z.literal("assistant"),
  z.literal("tool"),
]);

export const ChatCompletionChunkDeltaSchema = z.object({
  content: z.string().nullish(),
  reasoning_content: z.string().nullish(),
  reasoning: z.string().nullish(),
  function_call: ChatCompletionChunkFunctionCallSchema.optional(),
  // Nullable: providers send `"role": null` on non-role deltas.
  role: contentRolesSchema.nullish(),
  tool_calls: z.array(ChatCompletionChunkToolCallSchema).optional(),
});

export type ChatCompletionChunkDelta = z.infer<
  typeof ChatCompletionChunkDeltaSchema
>;

export const ChatCompletionChunkChoiceSchema = z.object({
  delta: ChatCompletionChunkDeltaSchema,
  finish_reason: FinishReasonSchema.or(z.null()),
  index: z.number().int(),
  // Nullable to match the wire format: OpenAI (and runtimes like SGLang)
  // send `"logprobs": null` on stream chunks when logprobs are not requested.
  logprobs: LogprobsSchema.nullable().optional(),
});

export type ChatCompletionChunkChoice = z.infer<
  typeof ChatCompletionChunkChoiceSchema
>;

export const ChatCompletionChunkSchema = z.object({
  id: z.string(),
  choices: z.array(ChatCompletionChunkChoiceSchema),
  created: z.number().int(),
  model: z.string(),
  object: z.literal("chat.completion.chunk"),
  system_fingerprint: z.string().optional(),
  // Nullable to match the wire format: with `stream_options.include_usage`,
  // choice-carrying chunks report `"usage": null` and only the final
  // usage-only chunk is populated.
  usage: CompletionUsageSchema.nullable().optional(),
});

export type ChatCompletionChunk = z.infer<typeof ChatCompletionChunkSchema>;

export const TextCompletionChunkChoiceSchema = z.object({
  text: z.string(),
  finish_reason: FinishReasonSchema.or(z.null()),
  index: z.number().int(),
  // Nullable to match the wire format (see ChatCompletionChunkChoiceSchema).
  logprobs: LogprobsSchema.nullable().optional(),
  matched_stop: z.string().nullish(),
});

export type TextCompletionChunkChoice = z.infer<
  typeof TextCompletionChunkChoiceSchema
>;

export const TextCompletionChunkSchema = z.object({
  id: z.string(),
  created: z.number().int(),
  choices: z.array(TextCompletionChunkChoiceSchema),
  model: z.string(),
  object: z.literal("text_completion"),
  system_fingerprint: z.string().optional(),
  // Nullable to match the wire format (see ChatCompletionChunkSchema).
  usage: CompletionUsageSchema.nullable().optional(),
});

export type TextCompletionChunk = z.infer<typeof TextCompletionChunkSchema>;

export type CompletionChunk =
  | TextCompletionChunk
  | ChatCompletionChunk
  | ResponseStreamChunk;
